'use client'

import { BankFeedsAddNewFeed } from '@repo/accounts/BankFeedsAddNewFeed'
import { BankFeedsCard } from '@repo/accounts/BankFeedsCard'
import { BankFeedsCreatePaymentLink } from '@repo/accounts/BankFeedsCreatePaymentLink'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { NewMatterIcon } from '@repo/ui/NewMatterIcon'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useBankFeedsStore } from '@repo/store/useBankFeedsStore'
import { useCallback, useEffect, useState } from 'react'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { usePlaidLink, PlaidLinkOnSuccess } from 'react-plaid-link'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

export const BankFeedsModule = ({ testData = false }) => {
  const [addNewBankFeedIsOpen, setAddNewBankFeedIsOpen] = useState(false)
  const [createPaymentLinkIsOpen, setCreatePaymentLinkIsOpen] = useState(false)
  const [currentBankFeedPaymentLinkId, setCurrentBankFeedPaymentLinkId] = useState(null)
  const [isFetching, setIsFetching] = useState(false)
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [newFeedMetadata, setNewFeedMetadata] = useState<any>(null)
  const [plaidSuccess, setPlaidSuccess] = useState(false)
  const [publicToken, setPublicToken] = useState<string | null>(null)

  const { roleId } = useAuthenticationStore()
  const { bankFeeds, bankFeedsHaveChanged, fetchAllBankFeeds, fetchGeneratedLinkToken, resetBankFeedsHaveChanged } =
    useBankFeedsStore()
  const { enableBankFeeds } = useLookupsStore()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()

  useEffect(() => {
    if (testData) return
    fetchAllBankFeeds()
  }, [fetchAllBankFeeds, bankFeedsHaveChanged])

  const onSuccess = useCallback<PlaidLinkOnSuccess>((public_token: any, metadata: any) => {
    setNewFeedMetadata(metadata)
    setPublicToken(public_token)
    setPlaidSuccess(true)
  }, [])

  const { open, exit, ready } = usePlaidLink({
    token: linkToken ?? '',
    onSuccess,
    onExit: (err: any) => {
      console.log('Plaid Link Exited. Error:', err)
      setPlaidSuccess(false)
      resetBankFeedsHaveChanged()
    },
    onEvent: () => {}
  })

  const handleAddNewBankFeed = async () => {
    if (testData) return

    try {
      setIsFetching(true)
      const newToken = await fetchGeneratedLinkToken()
      setLinkToken(newToken)
    } catch (error) {
      console.error('Error fetching generated link token:', error)
      setIsFetching(false)
    }
  }

  useEffect(() => {
    if (testData) return

    if (linkToken && ready) {
      open()
      setIsFetching(false)
    }
  }, [linkToken, ready, open])

  useEffect(() => {
    if (testData) return

    if (linkToken && newFeedMetadata && plaidSuccess) {
      setAddNewBankFeedIsOpen(true)
      setPlaidSuccess(false)
    }
  }, [linkToken, newFeedMetadata, setAddNewBankFeedIsOpen, plaidSuccess])

  if (!enableBankFeeds) return <MUIStack data-testid="BankFeedsModule"></MUIStack>

  return (
    <MUIStack data-testid="BankFeedsModule" sx={{ mt: pageTitleBlockIsVisible === true ? 0 : '10rem' }}>
      {!bankFeeds || bankFeeds.length === 0 ? (
        <MUIStack direction="column" alignItems="center" sx={{ mt: '5rem' }}>
          <NewMatterIcon sx={{ height: '5rem', width: '5rem' }} />
          <MUITypography variant="h3" sx={{ mt: '3rem' }}>
            No bank feeds
          </MUITypography>
          <MUITypography variant="body1" sx={{ mt: '0.5rem', color: 'text.secondary' }}>
            Click the add new button below to add a bank feed
          </MUITypography>
          <MUIButton
            onClick={handleAddNewBankFeed}
            variant="contained"
            sx={{ mt: '2rem' }}
            disabled={roleId !== 1 ? true : isFetching || (!!linkToken && !ready)}
          >
            {isFetching ? 'Preparing…' : 'Add new'}
          </MUIButton>
        </MUIStack>
      ) : (
        <>
          <MUIButton
            onClick={handleAddNewBankFeed}
            variant="contained"
            sx={{
              mt: '2rem',
              position: 'absolute',
              right: pageTitleBlockIsVisible ? '5rem' : '2rem',
              top: pageTitleBlockIsVisible ? '9rem' : '3.5rem',
              zIndex: 10
            }}
            disabled={roleId !== 1 ? true : isFetching || (!!linkToken && !ready)}
          >
            {isFetching ? 'Preparing…' : 'Add new'}
          </MUIButton>
          <MUIStack
            alignSelf={'center'}
            display="grid"
            gap={6}
            justifyItems="center"
            sx={{
              m: '0 4rem 4rem',
              width: 'calc(100% - 8rem)',
              maxWidth: '1200px',
              gridTemplateColumns: 'repeat(3, 1fr)',
              [`@media only screen and (max-width: 1200px)`]: {
                gridTemplateColumns: 'repeat(2, 1fr)',
                maxWidth: '800px'
              },
              [`@media only screen and (max-width: 800px)`]: {
                gridTemplateColumns: 'repeat(1, 1fr)',
                maxWidth: '600px'
              }
            }}
          >
            {bankFeeds.map((feed: any, index: number) => (
              <BankFeedsCard
                key={index}
                bankFeed={feed}
                setCreatePaymentLinkOpen={setCreatePaymentLinkIsOpen}
                isCreatePaymentLinkOpen={createPaymentLinkIsOpen}
                setCurrentBankFeedPaymentLinkId={setCurrentBankFeedPaymentLinkId}
                currentBankFeedPaymentLinkId={currentBankFeedPaymentLinkId}
              />
            ))}
          </MUIStack>
        </>
      )}
      <BankFeedsAddNewFeed
        isOpen={addNewBankFeedIsOpen}
        setOpen={setAddNewBankFeedIsOpen}
        metadata={newFeedMetadata}
        setMetadata={setNewFeedMetadata}
        setToken={setPublicToken}
        token={publicToken}
      />
      <BankFeedsCreatePaymentLink
        bankFeed={currentBankFeedPaymentLinkId ?? null}
        isOpen={createPaymentLinkIsOpen}
        setOpen={setCreatePaymentLinkIsOpen}
      />
    </MUIStack>
  )
}
